import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * Generic row-selection state and helpers for table UIs.
 */
export function useRowSelection({
  externalSelectedRows = [],
  onSelectedRowsChange,
  filteredRowIds = [],
  clearSelectionDeps = [],
}) {
  const [localSelectedRows, setLocalSelectedRows] = useState([]);

  const selectedRows = onSelectedRowsChange ? externalSelectedRows : localSelectedRows;
  const setSelectedRows = onSelectedRowsChange || setLocalSelectedRows;

  useEffect(() => {
    setSelectedRows([]);
    // Intentionally spread dependency list provided by caller.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, clearSelectionDeps);

  const handleSelectAll = useCallback(() => {
    if (filteredRowIds.length === 0) {
      return;
    }

    setSelectedRows((prev) => {
      // UX policy: if there's any selection at all, the header checkbox acts as "clear selection" first.
      // Only when there is no selection does it act as "select all (visible)".
      if (Array.isArray(prev) && prev.length > 0) {
        return [];
      }

      return Array.from(new Set(filteredRowIds));
    });
  }, [filteredRowIds, setSelectedRows]);

  const handleSelectRow = useCallback(
    (event, rowId) => {
      event.stopPropagation();
      if (event.target.checked) {
        setSelectedRows((prev) => [...prev, rowId]);
      } else {
        setSelectedRows((prev) => prev.filter((id) => id !== rowId));
      }
    },
    [setSelectedRows]
  );

  const isRowSelected = useCallback(
    (rowId) => selectedRows.includes(rowId),
    [selectedRows]
  );

  const filteredRowCount = filteredRowIds.length;
  const selectedRowCount = selectedRows.length;

  const selectedFilteredCount = useMemo(() => {
    if (selectedRowCount === 0 || filteredRowCount === 0) {
      return 0;
    }

    const filteredSet = new Set(filteredRowIds);
    return selectedRows.reduce((count, id) => {
      return filteredSet.has(id) ? count + 1 : count;
    }, 0);
  }, [filteredRowCount, filteredRowIds, selectedRowCount, selectedRows]);

  const isAllSelected =
    filteredRowCount > 0 && selectedFilteredCount === filteredRowCount;
  const isSomeSelected =
    selectedFilteredCount > 0 && selectedFilteredCount < filteredRowCount;

  const clearSelection = useCallback(() => setSelectedRows([]), [setSelectedRows]);

  return {
    selectedRows,
    setSelectedRows,
    clearSelection,
    selectedRowCount,
    filteredRowCount,
    selectedFilteredCount,
    isAllSelected,
    isSomeSelected,
    handleSelectAll,
    handleSelectRow,
    isRowSelected,
  };
}
